// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import clsx from 'clsx'
import { REPLY_LINK_TEXT, replyLinkHref } from 'lib/community/model'
import { newTabProps, usePrefersNewTab } from 'lib/community/use-writer-handoff'
import styles from './ReviewEngagementRow.module.css'

interface ReplyLinkProps {
  /** The review's author handle (no '@') — the link's accessible name. */
  handle: string
  /** `replyLinkHref(thread)`. */
  href: string
  onClick?: () => void
}

/** "Reply →" to a review's thread at its reply box: same tab on the server pass and phones, a
 *  new tab on a desktop after mount (the accept result's convention). */
export const ReplyLink: React.VFC<ReplyLinkProps> = ({ handle, href, onClick }) => {
  const newTab = usePrefersNewTab()
  return (
    <a href={href} className={styles.reply} onClick={onClick} {...newTabProps(newTab)}>
      {REPLY_LINK_TEXT}
      <span className={styles.srOnly}>{` to @${handle}’s owner review`}</span>
      <span aria-hidden="true">{' →'}</span>
    </a>
  )
}

interface ReviewReplyRowProps {
  handle: string
  /** The review's thread (`communityThreadHref`); null → nothing. */
  thread: string | null
  /** The host's analytics for the click (its page's existing event). */
  onReply?: () => void
  className?: string
}

/**
 * The compact "Reply →" line under an owner review on the card surfaces (card Owners block,
 * guide roster, review-post card — owner funnel C11): one 12px line that never wraps. Reply
 * only: those payloads carry no `helpful_count` (DealHunt `cardNotes`). Kept apart from
 * `ReviewEngagementRow` so those pages do not load the Helpful control they never render.
 */
const ReviewReplyRow: React.VFC<ReviewReplyRowProps> = ({ handle, thread, onReply, className }) => {
  const href = replyLinkHref(thread)
  if (!href) {
    return null
  }
  return (
    <div className={clsx(styles.row, styles.compact, className)}>
      <ReplyLink handle={handle} href={href} onClick={onReply} />
    </div>
  )
}

export default ReviewReplyRow
